import * as Sentry from '@sentry/nextjs'
import axios from 'axios'

import { type TrackUserRequest, getCrmServiceClient } from '@/clients/crm-service'

import { baseAxiosConfig } from './api/rest/config'
import { getExperiments } from './attribution/experimentAssignments'
import { getStoredLeadId } from './attribution/leadId'
import type { BrazeCustomEventName } from './braze/brazeConstants'
import { getGtmServerPreviewToken } from './checkout/gtmServerPreview'

const axiosInstance = axios.create({ ...baseAxiosConfig, baseURL: process.env.NEXT_PUBLIC_CRM_SERVICE_BASE })

const client = getCrmServiceClient(axiosInstance)

export const trackCrmUser = async (
  email: string,
  customEventName: BrazeCustomEventName,
  marketingConsent: boolean,
  additionalFields?: Record<string, unknown>,
  customEventProperties?: Record<string, unknown>,
) => {
  let trackUserRequest: TrackUserRequest
  if (!!additionalFields || !!customEventProperties) {
    trackUserRequest = {
      email,
      customEventName,
      marketingConsent,
      ...(additionalFields && { additionalFields }),
      ...(customEventProperties && { customEventProperties }),
    }
  } else {
    trackUserRequest = { email, customEventName, marketingConsent }
  }
  await client.trackUser(trackUserRequest)
}

// Best-effort: flag the attribution row for the current lead_id as captured
// (email submitted). No-op when the session has no lead_id. Never throws — it
// must never break email capture.
export const recordAttributionLeadCaptured = async (leadId: string | null = getStoredLeadId()): Promise<void> => {
  if (!leadId) return
  try {
    await client.recordLeadCaptured({ leadId, experiments: getExperiments() ?? undefined })
  } catch (error) {
    // attribution is best-effort — log the failure but never throw
    Sentry.captureException(error, { extra: { leadId, attributionCall: 'lead-captured' } })
  }
}

// Best-effort: record the purchased product + value against the attribution row.
// Never throws — it must never break the order confirmation page.
// Currency is uppercased here (single chokepoint): Stripe sessions carry
// lowercase codes ("gbp") but crm-service validates uppercase ISO-4217 ("GBP")
// and rejects lowercase with a 400.
export const recordAttributionPurchase = async (
  leadId: string,
  product: string,
  value: number,
  currency?: string | null,
): Promise<void> => {
  try {
    await client.recordPurchase({
      leadId,
      product,
      value,
      currency: currency?.toUpperCase() ?? null,
      experiments: getExperiments() ?? undefined,
    })
  } catch (error) {
    // attribution is best-effort — log the failure but never throw
    Sentry.captureException(error, { extra: { leadId, attributionCall: 'purchase' } })
  }
}

type MarketingEvent = {
  eventName: string
  /** Omit for events that should always be forwarded. */
  dedupeKey?: string
  event: Record<string, unknown>
}

/**
 * Sends an event to crm-service for forwarding to the GTM server container. `event` is passed
 * through untouched apart from the IP and user agent crm-service injects, so it must already be in
 * the container's shape.
 *
 * Fire and forget: the 202 says nothing about whether the container took the event, and crm-service
 * reports a failed forward to Sentry itself. What is captured here is the narrower case it cannot
 * see — crm-service being unreachable.
 *
 * A captured GTM preview token rides along as `X-Gtm-Server-Preview`, which crm-service passes to
 * the container so the event lands in that preview session instead of firing tags.
 *
 * Returns whether the request reached crm-service, so the caller can release its browser dedup
 * marker and let a later attempt retry when crm-service was unreachable.
 *
 * Not on the generated client because the endpoint is not in a deployed OpenAPI spec yet; swap to
 * `client` once `yarn generate-api` picks it up.
 */
export const sendMarketingEvent = async ({ eventName, dedupeKey, event }: MarketingEvent): Promise<boolean> => {
  try {
    const previewToken = getGtmServerPreviewToken()
    await axiosInstance.post(
      '/public/attribution/marketing/events',
      { eventName, dedupeKey, event },
      previewToken ? { headers: { 'X-Gtm-Server-Preview': previewToken } } : undefined,
    )
    return true
  } catch (error) {
    Sentry.captureException(error, { extra: { eventName, dedupeKey, crmCall: 'marketing-event' } })
    return false
  }
}
